import React, { useEffect, useState } from "react"
import { ColumnDef } from "@tanstack/react-table"
import useDataTable from "hooks/useDataTable"
import { useDispatch, useSelector } from "react-redux"
import { createSelector } from "reselect"
import { FormikHelpers, useFormik } from "formik"
import * as Yup from "yup"
import DataTableTemplate from "Components/templates/data-table-template"
import Modal from "Components/molecule/modal"
import IconButton from "Components/atoms/buttons/icon-button"
import PrimaryButton from "Components/atoms/buttons/primary-button"
import TextInput from "Components/molecule/inputs/text-input"
import TableRowControlsContainer from "Components/atoms/containers/table-row-controls-container"
import { PencilIcon } from "Components/atoms/icons"
import { HealthRule, healthRuleLabel } from "models/health-rule"
import { AdminProjectHealthRulesSliceState, setRule, setRules } from "slices/admin/project-health-rules/reducer"
import { getHealthRuleHistory, getHealthRules, submitHealthRuleValue } from "slices/admin/project-health-rules/thunk"
import { formatDateTimeAmPm } from "utils/tools"

const selectRules = createSelector(
    (state: any) => state.AdminProjectHealthRules,
    ({ rules }: AdminProjectHealthRulesSliceState) => ({ rules })
)

interface RuleFields {
    value: number | ''
}

const RuleValidation = Yup.object({
    value: Yup.number().typeError("Must be a number").required("Value is required").min(0, "Must be 0 or more"),
})

const ProjectHealthRulesPage = () => {
    document.title = "Project Health Rules | IbexIQ";
    const dispatch: any = useDispatch()
    const { rules } = useSelector(selectRules)

    const [isLoading, setIsLoading] = useState<boolean>(true)
    const [editingRule, setEditingRule] = useState<HealthRule | null>(null)
    const [history, setHistory] = useState<HealthRule[]>([])

    useEffect(() => {
        const init = async () => {
            const res = await getHealthRules()
            if (res) dispatch(setRules(res))
            setIsLoading(false)
        }
        init()
    }, [])

    const onSubmit = async (vals: RuleFields, helpers: FormikHelpers<RuleFields>) => {
        if (!editingRule || vals.value === '') return
        helpers.setSubmitting(true)
        const res = await submitHealthRuleValue(editingRule.key, Number(vals.value))
        if (res) {
            dispatch(setRule(res))
            handleCloseModal()
        }
        helpers.setSubmitting(false)
    }

    const { values, errors, touched, handleBlur, handleChange, handleSubmit, setValues, resetForm, isSubmitting, isValid } =
        useFormik<RuleFields>({ initialValues: { value: '' }, onSubmit, validationSchema: RuleValidation })

    const handleEditRule = async (rule: HealthRule) => {
        setEditingRule(rule)
        setValues({ value: rule.value }, false)
        setHistory([])
        const res = await getHealthRuleHistory(rule.key)
        if (res) setHistory(res)
    }

    const handleCloseModal = () => {
        setEditingRule(null)
        setHistory([])
        resetForm()
    }

    const columns = React.useMemo<ColumnDef<HealthRule>[]>(() => [
        {
            header: "Rule",
            id: "rule",
            size: 220,
            accessorFn: (r) => healthRuleLabel(r.key),
            cell: ({ row }) => <strong>{healthRuleLabel(row.original.key)}</strong>,
        },
        {
            header: "Description",
            accessorKey: "description",
            size: 340,
            meta: { getCellStyle: () => ({ whiteSpace: 'normal' }) },
        },
        {
            header: "Value",
            accessorKey: "value",
            size: 90,
            meta: { getHeaderClassName: () => 'text-end', getCellClassName: () => 'text-end' },
            cell: ({ row }) => row.original.value.toLocaleString('en-CA'),
        },
        {
            header: "Version",
            accessorKey: "version",
            size: 80,
            cell: ({ row }) => row.original.version === 0 ? <span className="text-muted">Default</span> : `v${row.original.version}`,
        },
        {
            header: "Updated",
            id: "updated",
            size: 220,
            accessorFn: (r) => r.updatedAt ? new Date(r.updatedAt).getTime() : undefined,
            cell: ({ row }) => <TableRowControlsContainer style={{ justifyContent: 'space-between' }}>
                <span>
                    {row.original.updatedBy ?? (row.original.version > 0 ? "System (migration)" : "—")}
                    {row.original.updatedAt && <><br /><small className="text-muted">{formatDateTimeAmPm(row.original.updatedAt)}</small></>}
                </span>
                <IconButton onClick={() => handleEditRule(row.original)}><PencilIcon /></IconButton>
            </TableRowControlsContainer>
        },
    ], [])

    const table = useDataTable({
        columns,
        data: rules,
        columnResizeMode: 'onEnd',
        enableColumnResizing: true
    })

    return <DataTableTemplate
        title="Admin"
        breadcrumbItem="Project Health Rules"
        table={table}
        columns={columns}
        isLoading={isLoading}
        modal={
            <Modal
                isOpen={!!editingRule}
                onClose={handleCloseModal}
                title={editingRule ? `Edit ${healthRuleLabel(editingRule.key)}` : ""}
            >
                {editingRule && <div className="generate-documents-form">
                    <p className="text-muted">{editingRule.description}</p>
                    <TextInput
                        type="number"
                        name="value"
                        label="Value*"
                        touched={!!touched.value}
                        value={values.value}
                        onBlur={handleBlur}
                        onChange={handleChange}
                        error={!!touched.value && !!errors.value}
                        message={touched.value ? errors.value : undefined}
                    />
                    <PrimaryButton
                        type="submit"
                        onClick={() => handleSubmit()}
                        disabled={isSubmitting || !isValid}
                        className="w-100"
                    >
                        {isSubmitting ? "Saving..." : "Save as new version"}
                    </PrimaryButton>

                    <h6 className="mt-4 mb-2">History</h6>
                    {history.length === 0
                        ? <small className="text-muted">No saved versions yet (code default in use).</small>
                        : <table className="table table-sm mb-0">
                            <thead><tr><th>Version</th><th className="text-end">Value</th><th>By</th><th>When</th></tr></thead>
                            <tbody>
                                {history.map(h => (
                                    <tr key={h.version}>
                                        <td>v{h.version}</td>
                                        <td className="text-end">{h.value.toLocaleString('en-CA')}</td>
                                        <td>{h.updatedBy ?? "System (migration)"}</td>
                                        <td>{formatDateTimeAmPm(h.effectiveFrom)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>}
                </div>}
            </Modal>
        }
    />
}

export default ProjectHealthRulesPage
